/**
 * TagFormModal - Modal para criar/editar etiquetas.
 */
import React, { useState, useEffect } from 'react';
import { Tag } from '../../../../types';
import { AppIcon } from '../../../../components/icons/AppIcon';
import Input from '../../../../components/ui/Input';
import { Button } from '../../../../components/ui/Button';
import Modal from '../../../../components/ui/Modal';

interface TagFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: Omit<Tag, 'id' | 'createdAt'>) => Promise<void>;
    tagToEdit?: Tag;
}

const COLORS = [
    '#3B82F6', '#10B981', '#EAB308', '#F59E0B', '#EF4444', '#A855F7', '#EC4899', '#06B6D4', '#6B7280'
];

const TagFormModal: React.FC<TagFormModalProps> = ({ isOpen, onClose, onSave, tagToEdit }) => {
    const [name, setName] = useState('');
    const [color, setColor] = useState(COLORS[0]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (tagToEdit) {
                setName(tagToEdit.name);
                setColor(tagToEdit.color);
            } else {
                setName('');
                setColor(COLORS[0]);
            }
        }
    }, [isOpen, tagToEdit]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await onSave({ name, color });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm">
            <Modal.Header onClose={onClose}>
                {tagToEdit ? 'Editar etiqueta' : 'Nova etiqueta'}
            </Modal.Header>
            <Modal.Body>
                <form id="tag-form" onSubmit={handleSubmit} className="space-y-5">
                    <Input
                        label="Nome da etiqueta"
                        name="name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Nome da etiqueta"
                        autoFocus
                    />

                    <fieldset>
                        <legend className="input-label mb-2">Cor da etiqueta</legend>
                        <div className="flex flex-wrap gap-2">
                            {COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-all ${color === c ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:opacity-80'}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setColor(c)}
                                    aria-label={`Cor ${c}`}
                                    aria-pressed={color === c}
                                >
                                    {color === c && (
                                        <AppIcon name="check" className="h-4 w-4 text-white drop-shadow" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </fieldset>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
                    Cancelar
                </Button>
                <Button type="submit" form="tag-form" disabled={isSaving}>
                    {isSaving ? 'Salvando...' : 'Salvar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TagFormModal;
